import { FC, useEffect, useState, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Box, Dialog, useSnackbar } from '@robocorp/components';

import {
  useCreateWorkItemMutation,
  useUploadWorkItemFileMutation,
  useStartProcessRunWithItemsMutation,
  useProcessQuery,
} from '~/queries';
import { JSONEditor } from '~/components';

import CreateWorkItemFile from './CreateWorkItemFile';

type Props = {
  /**
   * Target process id
   */
  processId: string;
  /**
   * Is dialog open state
   */
  open: boolean;
  /**
   * Dialog close callback used
   */
  onClose: () => void;
  /**
   * Should the process be run by default
   */
  runByDefault?: boolean;
};

const DEFAULT_PAYLOAD = JSON.stringify({ '': '' }, null, 2);

/**
 * Create a new work item payload dialog
 */
const CreateWorkItem: FC<Props> = ({ open, onClose, processId, runByDefault }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [payload, setPayload] = useState<string>(DEFAULT_PAYLOAD);

  const { t } = useTranslation();
  const { addSnackbar } = useSnackbar();
  const [payloadError, setPayloadError] = useState<boolean>(false);

  const createWorkItemMutation = useCreateWorkItemMutation();
  const uploadWorkItemFileMutation = useUploadWorkItemFileMutation();
  const startProcessRunWithItemsMutation = useStartProcessRunWithItemsMutation();
  const { data: process } = useProcessQuery(processId);

  // Set default data
  useEffect(() => {
    try {
      if (open && process?.config?.defaultRun?.customData) {
        setPayload(process.config.defaultRun.customData);
        setFiles([]);
      }
    } catch (_err) {
      // Process should not have invalid default data payload defined
    }
  }, [process, open]);

  const onCreateWorkItem = useCallback(async () => {
    const workItem = await createWorkItemMutation.mutateAsync({
      processId,
      payload: JSON.parse(payload),
    });

    if (workItem) {
      try {
        if (!runByDefault) {
          addSnackbar({ message: t('createWorkItemSuccess'), variant: 'success' });
        }

        const fileUploadPromises = files.map((file) => {
          return uploadWorkItemFileMutation.mutateAsync(
            {
              workItemId: workItem.id,
              isNewWorkItem: true,
              file,
            },
            {
              onError: () => {
                throw new Error('Upload failed');
              },
            },
          );
        });

        await Promise.all(fileUploadPromises);

        if (runByDefault) {
          startProcessRunWithItemsMutation.mutate(
            { processId, workItemIds: [workItem.id] },
            {
              onSuccess: () => {
                addSnackbar({
                  message: t('startProcessWithNewWorkItemSuccess'),
                  variant: 'success',
                });
              },
            },
          );
        }
      } catch (_err) {
        // TODO: Delete newly created work item if file upload fails
      }

      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [process, processId, files, payload]);

  const onCloseDialog = useCallback(() => {
    if (!createWorkItemMutation.isLoading) {
      onClose();
    }
  }, [createWorkItemMutation.isLoading, onClose]);

  const isLoading = createWorkItemMutation.isLoading || uploadWorkItemFileMutation.isLoading;

  return (
    <Dialog width={580} open={open} onClose={onCloseDialog}>
      <Dialog.Header>
        <Dialog.Header.Title title={runByDefault ? t('runProcess') : t('createWorkItem')} />
      </Dialog.Header>
      <Dialog.Content>
        <Box mb="$24">
          <JSONEditor
            title={t('jsonEditor.defaultData')}
            value={payload}
            onChange={setPayload}
            error={payloadError}
            setError={setPayloadError}
          />
        </Box>
        <CreateWorkItemFile files={files} setFiles={setFiles} process={runByDefault} />
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          type="button"
          loading={isLoading}
          onClick={onCreateWorkItem}
          disabled={payloadError}
        >
          {runByDefault ? t('process.run') : t('create')}
        </Button>
        <Button type="button" disabled={isLoading} onClick={onCloseDialog} variant="secondary">
          {t('cancel')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export default memo(CreateWorkItem);
